import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import Pagination from '../components/main/Pagination';
import useSalesHistoryApi from '../api/useSalesHistoryApi';
import SalesColumnChart from '../components/saleshistory/SalesColumnChart';
import { debounce } from '../helper/debounce';
import CommonLoader from '../components/loaders/CommonLoader';
import { Avatar } from '@mui/material';
import dayjs from 'dayjs';
import SalesPieChart from '../components/saleshistory/SalesPieChart';
import ViewSales from '../components/modals/ViewSales';
import AdminWiseDetailsModal from '../components/modals/AdminWiseDetailsModal';
import { useAuth } from '../contexts/AuthContext';

const SalesHistory = () => {
    const { getStates, getSalesData, loading } = useSalesHistoryApi();
    const { profile } = useAuth();
    const [filter, setFilter] = useState("daily");
    const [start_date, setStartDate] = useState("");
    const [end_date, setEndDate] = useState("");
    const [states, setStates] = useState(null);
    const [data, setData] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [page, setPage] = useState(1);
    const [total_pages, setTotalPages] = useState(1);
    const [selected, setSelected] = useState(null);
    const [openView, setOpenView] = useState(false);
    const [openAdminWiseDetails, setOpenAdminWiseDetails] = useState(false);

    const formatDate = (date) => date.toISOString().split("T")[0];

    useEffect(() => {
        const daily = new Date();
        let start, end;

        switch (filter) {
            case "daily":
                start = end = daily;
                break;
            case "weekly":
                start = new Date(daily);
                start.setDate(daily.getDate() - 6);
                end = daily;
                break;
            case "monthly":
                start = new Date(daily.getFullYear(), daily.getMonth(), 1);
                end = new Date(daily.getFullYear(), daily.getMonth() + 1, 0);
                break;
            case "yearly":
                start = new Date(daily.getFullYear(), 0, 1);
                end = new Date(daily.getFullYear(), 11, 31);
                break;
            case "custom":
                return; // Don't auto-set dates for custom filter
            default:
                return;
        }

        setStartDate(formatDate(start));
        setEndDate(formatDate(end));
    }, [filter]);

    const handleFilterChange = (e) => {
        const value = e.target.value;
        setFilter(value);
    };

    const handleDateChange = (e, dateType) => {
        const value = e.target.value;
        if (dateType === 'start') {
            setStartDate(value);
            // If end date is before start date, update end date
            if (end_date && value > end_date) {
                setEndDate(value);
            }
        } else {
            // Don't allow end date to be before start date
            if (!start_date || value >= start_date) {
                setEndDate(value);
            }
        }
    };

    const handleClear = () => {
        setFilter("daily");
        setStartDate("");
        setEndDate("");
    };

    const fetchStates = async () => {
        try {
            const params = { filter };
            if (filter === 'custom' && start_date && end_date) {
                params.start_date = start_date;
                params.end_date = end_date;
            }
            const res = await getStates(params);
            setStates(res?.data?.payload?.calculated_info);
        } catch (error) {
            console.error("Error fetching states:", error);
        }
    }

    useEffect(() => {
        fetchStates();
    }, [filter, start_date, end_date]);

    const fetchWithdrawals = async (page = 1, search = '') => {
        const res = await getSalesData(page, search);
        setData(res.data.payload.all_sales);
        setTotalPages(res.data.payload.total_pages);
    };

    const debouncedfetchWithdrawals = useCallback(
        debounce((value) => {
            fetchWithdrawals(1, value);
            setPage(1);
        }, 500),
        []
    );

    const handleSearchChange = (e) => {
        const value = e.target.value;
        setSearchTerm(value);
        debouncedfetchWithdrawals(value);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
    };

    useEffect(() => {
        fetchWithdrawals(page, searchTerm);
    }, [page]);

    const chartData = states?.second_chart_data || {};

    const totalSales = Object.values(chartData).reduce(
        (sum, item) => sum + (item?.amount || 0),
        0
    );
    return (
        <div>
            <div className='card-header'>
                <div className='d-flex align-items-center flex-wrap gap-2 justify-content-between mb-24'>
                    <div className='d-flex flex-wrap align-items-center justify-content-between gap-3 '>
                        <h6 className='fw-semibold mb-0'>Sales History</h6>
                    </div>
                    <div className="d-flex gap-2 flex-wrap align-items-center">
                        {profile?.user_type === 'superadmin' && (
                            <button
                                className="btn btn-sm btn-primary-600 px-16 py-8 radius-8 d-flex align-items-center gap-2"
                                onClick={() => setOpenAdminWiseDetails(true)}
                            >
                                <Icon icon="solar:chart-outline" className="text-lg" />
                                View Admin Wise Details
                            </button>
                        )}
                        <select
                            className='form-select form-select-sm w-auto bg-base border text-secondary-light'
                            value={filter}
                            onChange={handleFilterChange}
                        >
                            <option value="daily">Daily</option>
                            <option value="weekly">Weekly</option>
                            <option value="monthly">Monthly</option>
                            <option value="yearly">Yearly</option>
                            <option value="custom">Custom</option>
                        </select>

                        {filter === "custom" && (
                            <>
                                <input
                                    type="date"
                                    className="form-control form-control-sm w-auto"
                                    value={start_date}
                                    onChange={(e) => handleDateChange(e, 'start')}
                                    max={end_date || undefined}
                                />
                                <span>to</span>
                                <input
                                    type="date"
                                    className="form-control form-control-sm w-auto"
                                    value={end_date}
                                    onChange={(e) => handleDateChange(e, 'end')}
                                    min={start_date || undefined}
                                />
                            </>
                        )}

                        {filter !== "weekly" && (
                            <button
                                className="btn btn-sm btn-outline-secondary"
                                onClick={handleClear}
                            >
                                Clear
                            </button>
                        )}
                    </div>
                </div>
            </div>
            {(states === null && loading) ?
                <div>
                    <CommonLoader />
                </div>
                :
                <div className='row mb-24'>
                    <div className="col-md-8 ">
                        <div className="card h-100 p-0">
                            <div className="card-body p-24">
                                <SalesColumnChart states={states} />
                            </div>
                        </div>
                    </div>
                    <div className="col-md-4">
                        <div className='card radius-16'>
                            <div className='card-body py-20'>
                                <p className='text-center text-secondary-light d-flex align-items-center gap-8 justify-content-center'>
                                    Total Sales:
                                    <span className='fw-semibold text-primary-light'>
                                        ₹ {totalSales}
                                    </span>
                                </p>
                                <div className='mt-40 mb-24 text-center position-relative mx-auto' style={{ maxWidth: '288px' }}>
                                    <SalesPieChart states={states} />
                                </div>

                                <div className='d-flex align-items-center flex-wrap gap-24 justify-content-between'>
                                    {Object.entries(chartData).map(([key, value]) => (
                                        <div key={key} className='d-flex flex-column align-items-start'>
                                            <span className='text-secondary-light text-sm fw-normal'>
                                                {key}
                                            </span>
                                            <h6 className='text-primary-light fw-semibold mb-0 mt-4 text-lg'>
                                                ₹ {value?.amount || 0}
                                            </h6>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            }
            {states !== null &&
                <div className="card radius-12 mt-24">
                    <div className="card-body p-24">
                        <form className="navbar-search mb-24">
                            <input
                                type="text"
                                className="bg-base h-40-px"
                                name="search"
                                placeholder="Search"
                                value={searchTerm}
                                onChange={handleSearchChange}
                            />
                            <Icon icon="ion:search-outline" className="icon" />
                        </form>
                        {loading ? (
                            <CommonLoader />
                        ) : (
                            <>
                                <div className="table-responsive scroll-sm">
                                    <table className="table bordered-table sm-table mb-0">
                                        <thead>
                                            <tr>
                                                <th className="text-start">User</th>
                                                <th className="text-center">Phone Number.</th>
                                                <th className="text-center">Admin</th>
                                                <th className="text-center">Repeat Count</th>
                                                <th className="text-center">Plan</th>
                                                <th className="text-center">Duration</th>
                                                <th className="text-center">Amount</th>
                                                <th className="text-center">Wallet</th>
                                                <th className="text-center">Razorpay</th>
                                                <th className="text-center">Payment Method</th>
                                                <th className="text-center">Purchase Date</th>
                                                <th className="text-center">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {data?.length > 0 ? (
                                                data.map((d) => (
                                                    <tr key={d._id}>
                                                        <td className="text-start">
                                                            <div className="d-flex align-items-center">
                                                                <Avatar
                                                                    src={d.user_id?.profile_photo}
                                                                    alt=""
                                                                    className="w-40-px h-40-px rounded-circle me-12"
                                                                />
                                                                <span className="text-md text-secondary-light">{d.user_id?.user_name}</span>
                                                            </div>
                                                        </td>
                                                        <td className="text-center">{d.user_id?.phone_number}</td>
                                                        <td className="text-center">{d.user_id?.parent_user_id?.user_name || "N/A"}</td>
                                                        <td className="text-center">{d.subscription_repeat_count || "0"}</td>
                                                        <td className="text-center">{d.plan_id?.title}</td>
                                                        <td className="text-center">{d.plan_id?.duration_in_days} Days</td>
                                                        <td className="text-center">₹ {d.amount}</td>
                                                        <td className="text-center">₹ {d.wallet_money_used || 0}</td>
                                                        <td className="text-center">₹ {d.razorpay_money_used || 0}</td>
                                                        <td className="text-center">{d.payment_method}</td>
                                                        <td className="text-center">
                                                            {dayjs(d.createdAt).format('DD MMM YYYY hh:mm A')}
                                                        </td>
                                                        <td className="text-center">
                                                            <div className="d-flex justify-content-center gap-2">
                                                                <button
                                                                    onClick={() => {
                                                                        setSelected(d);
                                                                        setOpenView(true);
                                                                    }}
                                                                    className="bg-info-focus bg-hover-info-200 text-info-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                >
                                                                    <Icon icon="majesticons:eye-line" className="text-xl" />
                                                                </button>
                                                                <Link
                                                                    to={`/users-list/${d.user_id?._id}`}
                                                                    target="_blank"
                                                                    className="bg-primary-focus bg-hover-primary-200 text-primary-600 w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                                                                >
                                                                    <Icon icon="solar:user-circle-linear" className="text-xl" />
                                                                </Link>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan="11" className="text-center py-4">No Users Found</td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </table>
                                </div>

                                <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
                                    <span>Page {page} of {total_pages}</span>
                                    <Pagination
                                        total_pages={total_pages}
                                        current_page={page}
                                        onPageChange={handlePageChange}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>
            }
            <ViewSales open={openView} onClose={() => setOpenView(false)} data={selected} />
            <AdminWiseDetailsModal
                open={openAdminWiseDetails}
                onClose={() => setOpenAdminWiseDetails(false)}
                filter="sales"
            />
        </div >
    );
};

export default SalesHistory
